"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { X, Star } from 'lucide-react';
import { apiRequest } from '../../utils/api';
import Cookies from 'js-cookie';

interface ReviewRequestPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onDismiss?: (suppressDays: number) => void;
}

const G2_REVIEW_URL = 'https://www.g2.com/products/dелфина/reviews?utm_source=g2&utm_medium=in_app_prompt&utm_campaign=review_generation';
const DISMISS_SUPPRESSION_DAYS = 30;

const ReviewRequestPrompt: React.FC<ReviewRequestPromptProps> = ({
  isOpen,
  onClose,
  onDismiss
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const isVisible = isOpen || isClosing;

  const handleDismiss = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      if (onDismiss) {
        onDismiss(DISMISS_SUPPRESSION_DAYS);
      }
      onClose();
    }, 200);
  }, [onClose, onDismiss]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const autoDismissTimer = setTimeout(() => {
      handleDismiss();
    }, 8000);

    return () => clearTimeout(autoDismissTimer);
  }, [isOpen, handleDismiss]);

  const handleLeaveReview = () => {
    if (onDismiss) {
      onDismiss(90);
    }
    window.open(G2_REVIEW_URL, '_blank', 'noopener,noreferrer');
    handleDismiss();
  };

  const handleClose = () => {
    handleDismiss();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pb-12">
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-200 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleClose}
      />

      <div
        className={`
          relative bg-white w-full max-w-lg border-2 border-black
          shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]
          transform transition-all duration-200
          ${isClosing ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'}
        `}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 z-10"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6 space-y-6">
          <div className="flex items-start gap-4">
            <div className="bg-positive-500/10 p-3 rounded-lg">
              <Star className="h-6 w-6 text-positive-500" />
            </div>
            <div className="flex-1 pr-8">
              <h2 className="text-xl font-bold text-gray-900">
                You are doing brilliantly.
              </h2>
              <p className="text-gray-600 mt-1">
                Mind sharing the love? It takes 60 seconds and helps others find us.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleLeaveReview}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-positive-600 hover:bg-positive-700 text-white font-bold py-3 px-6 border-2 border-ink shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-none transition-all duration-200"
            >
              Leave a review on G2
            </button>

            <button
              onClick={handleDismiss}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-bold py-3 px-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-none transition-all duration-200"
            >
              Not right now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewRequestPrompt;

export async function recordReviewSuppression(userId: string, suppressionType: string, expiresAt: Date): Promise<void> {
  try {
    await apiRequest('POST', '/review/suppressions', {
      user_id: userId,
      suppression_type: suppressionType,
      expires_at: expiresAt.toISOString()
    });
  } catch (error) {
    console.error('Error recording review suppression:', error);
  }
}

export async function checkReviewSuppression(userId: string): Promise<{ isSuppressed: boolean; expiresAt?: Date }> {
  try {
    const response = await apiRequest('GET', `/review/suppressions?user_id=${userId}`) as {
      is_suppressed: boolean;
      expires_at?: string;
    };

    if (response.is_suppressed && response.expires_at) {
      const expiresAt = new Date(response.expires_at);
      if (expiresAt > new Date()) {
        return { isSuppressed: true, expiresAt };
      }
    }
    return { isSuppressed: false };
  } catch (error) {
    console.error('Error checking review suppression:', error);
    return { isSuppressed: false };
  }
}

export async function checkHappyScore(): Promise<{ meetsThreshold: boolean; score?: number }> {
  try {
    const response = await apiRequest('GET', '/review/happy-score') as {
      score: number;
      meets_threshold: boolean;
    };

    return {
      meetsThreshold: response.meets_threshold,
      score: response.score
    };
  } catch (error) {
    console.error('Error checking happy score:', error);
    return { meetsThreshold: false };
  }
}